export async function captureImageFromCamera(deps = {}) {
  return captureStillImage({
    filenamePrefix: "camera",
    extension: "jpg",
    mimeType: "image/jpeg",
    startStream: async (navigatorValue) => navigatorValue.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: "environment"
      }
    }),
    ...deps
  });
}

export async function captureScreenshot(deps = {}) {
  return captureStillImage({
    filenamePrefix: "screenshot",
    extension: "png",
    mimeType: "image/png",
    startStream: async (navigatorValue) => navigatorValue.mediaDevices.getDisplayMedia({
      audio: false,
      video: true
    }),
    ...deps
  });
}

export async function startMicrophoneCapture(deps = {}) {
  const navigatorValue = deps.navigator || globalThis.navigator;
  const MediaRecorderValue = deps.MediaRecorder || globalThis.MediaRecorder;
  const BlobValue = deps.Blob || globalThis.Blob;
  const FileValue = deps.File || globalThis.File;
  if (!navigatorValue?.mediaDevices?.getUserMedia || typeof MediaRecorderValue !== "function") {
    throw new Error("Microphone capture is not supported in this browser.");
  }
  const stream = await navigatorValue.mediaDevices.getUserMedia({ audio: true, video: false });
  const chunks = [];
  const options = selectRecorderOptions(MediaRecorderValue);
  const recorder = new MediaRecorderValue(stream, options);
  let canceled = false;
  let settled = false;
  let stopResolve = null;
  let stopReject = null;
  const stopped = new Promise((resolve, reject) => {
    stopResolve = resolve;
    stopReject = reject;
  });

  recorder.addEventListener("dataavailable", (event) => {
    if (event?.data && Number(event.data.size || 0) > 0) {
      chunks.push(event.data);
    }
  });
  recorder.addEventListener("error", (event) => {
    if (settled) {
      return;
    }
    settled = true;
    stopTracks(stream);
    stopReject(event?.error || new Error("Microphone capture failed."));
  });
  recorder.addEventListener("stop", () => {
    stopTracks(stream);
    if (settled) {
      return;
    }
    if (canceled) {
      settled = true;
      stopReject(new Error("Microphone capture canceled."));
      return;
    }
    if (chunks.length === 0) {
      settled = true;
      stopReject(new Error("Microphone capture produced no audio."));
      return;
    }
    const mimeType = String(recorder.mimeType || options.mimeType || "audio/webm");
    const extension = audioExtensionForMimeType(mimeType);
    const blob = new BlobValue(chunks, { type: mimeType });
    settled = true;
    stopResolve(createFile(blob, `microphone-${Date.now()}.${extension}`, mimeType, FileValue));
  });

  recorder.start();
  return {
    async finish() {
      if (recorder.state !== "inactive") {
        recorder.stop();
      }
      return stopped;
    },
    cancel() {
      canceled = true;
      if (recorder.state !== "inactive") {
        recorder.stop();
      } else {
        stopTracks(stream);
      }
    }
  };
}

async function captureStillImage(options) {
  const navigatorValue = options.navigator || globalThis.navigator;
  const documentValue = options.document || globalThis.document;
  const FileValue = options.File || globalThis.File;
  if (!navigatorValue?.mediaDevices || typeof options.startStream !== "function") {
    throw new Error("Media capture is not supported in this browser.");
  }
  const stream = await options.startStream(navigatorValue);
  try {
    const video = documentValue.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    if (typeof video.play === "function") {
      await video.play();
    }
    await waitForVideoFrame(video);
    const canvas = documentValue.createElement("canvas");
    const width = Number(video.videoWidth || 1280);
    const height = Number(video.videoHeight || 720);
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Image capture is not supported in this browser.");
    }
    context.drawImage(video, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, options.mimeType);
    return createFile(blob, `${options.filenamePrefix}-${Date.now()}.${options.extension}`, options.mimeType, FileValue);
  } finally {
    stopTracks(stream);
  }
}

function waitForVideoFrame(video) {
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const cleanup = () => {
      video.removeEventListener?.("loadedmetadata", onLoaded);
      video.removeEventListener?.("loadeddata", onLoaded);
    };
    video.addEventListener?.("loadedmetadata", onLoaded);
    video.addEventListener?.("loadeddata", onLoaded);
  });
}

function canvasToBlob(canvas, mimeType) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to capture media."));
        return;
      }
      resolve(blob);
    }, mimeType);
  });
}

function selectRecorderOptions(MediaRecorderValue) {
  const supports = typeof MediaRecorderValue.isTypeSupported === "function"
    ? MediaRecorderValue.isTypeSupported.bind(MediaRecorderValue)
    : () => false;
  for (const mimeType of [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg"
  ]) {
    if (supports(mimeType)) {
      return { mimeType };
    }
  }
  return {};
}

function audioExtensionForMimeType(mimeType) {
  if (mimeType.includes("mp4")) {
    return "m4a";
  }
  if (mimeType.includes("ogg")) {
    return "ogg";
  }
  return "webm";
}

function stopTracks(stream) {
  for (const track of stream?.getTracks?.() || []) {
    track.stop?.();
  }
}

function createFile(blob, filename, mimeType, FileValue) {
  if (typeof FileValue === "function") {
    return new FileValue([blob], filename, { type: mimeType });
  }
  blob.name = filename;
  blob.lastModified = Date.now();
  return blob;
}
